<!--
 * @Descripttion: 微信绑定弹窗（公共组件，App.vue 全局挂载一次）
 *   用途：未绑定微信（users.open_id 为空）的账号点击支付时，先引导完成微信绑定，
 *   避免把空支付参数交给微信 JSAPI（否则微信会弹「调用支付JSAPI缺少参数:appId」）。
 *
 *   流程复用的都是线上既有链路，不新增后端协议：
 *     1) 扫码授权：/yljx/public/getQrcode 拿 ticket → 渲染公众号二维码
 *        → /yljx/public/lunxun2 轮询扫码结果，拿到微信账号（含 open_id）；
 *     2) 绑定手机号：/yljx/public/send 发验证码 → /yljx/public/telBind 绑定
 *        （后端口径：同手机号的纯手机号旧账号会被停用，返回文案已提示联系客服变更）；
 *     3) 绑定成功后把返回的微信账号写入登录态；
 *        - 扫码付款场景（openWechatBindDialog 传入 onBound）：**不整页刷新**，
 *          回调 onBound 就地续跑原付款流程（重新生成付款码）；
 *        - 未传 onBound（如微信内 JSAPI 入口）：保持原行为，提示 + 1.5s 整页刷新。
 *
 *   弹窗通过 utils/wechatPay 的注册器暴露 openWechatBindDialog()，
 *   任意页面/组件都可在「支付前预检失败」时唤起它。
-->
<template>
  <el-dialog
    title="绑定微信"
    :visible.sync="dialogVisible"
    width="80%"
    append-to-body
    :close-on-click-modal="false"
    :before-close="handleClose">
    <p class="wxbind-intro">
      您的账号尚未绑定微信，完成绑定后即可继续扫码付款。
    </p>

    <!-- 第一步：扫码授权 -->
    <div v-if="step === 'qrcode'" class="wxbind-body">
      <div class="wxbind-qr">
        <img v-if="qrImg" :src="qrImg" alt="微信授权二维码">
        <span v-else class="wxbind-qr-loading">二维码生成中…</span>
      </div>
      <p class="wxbind-tip">{{ qrTip }}</p>
    </div>

    <!-- 第二步：绑定手机号 -->
    <div v-else class="wxbind-body">
      <p class="wxbind-tip">微信授权成功，请补充手机号完成绑定：</p>
      <dl class="wxbind-field">
        <dt>手机号</dt>
        <dd>
          <el-input v-model="tel" maxlength="11" placeholder="请输入手机号"></el-input>
        </dd>
      </dl>
      <dl class="wxbind-field">
        <dt>验证码</dt>
        <dd class="wxbind-code">
          <el-input v-model="yzm" maxlength="6" placeholder="请输入验证码"></el-input>
          <span class="wxbind-send" :class="{ 'is-disabled': sending }" @click="sendCode">{{ sendText }}</span>
        </dd>
      </dl>
      <p class="wxbind-note">
        绑定后原手机号账号将停止使用；若该账号已有付费项目，请按提示联系客服变更到当前微信账号。
      </p>
      <div class="wxbind-actions">
        <el-button size="small" @click="restartQrcode">重新扫码</el-button>
        <el-button size="small" type="primary" :loading="binding" @click="submitBind">确认绑定</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { Message } from 'element-ui'
import apiReq from '@t/api'
import { registerWechatBindDialog, readCurrentUser, isWechatBrowser, SCAN_PAY_TEXT } from '@t/utils/wechatPay'

/** 扫码结果轮询间隔：1.5 秒（比登录弹窗稍慢，绑定场景对实时性要求不高） */
const POLL_INTERVAL = 1500
/** 验证码重发倒计时秒数，与登录弹窗保持一致 */
const RESEND_SECONDS = 60

export default {
  name: 'WechatBindDialog',
  data () {
    return {
      dialogVisible: false,
      step: 'qrcode',
      ticket: '',
      qrImg: '',
      wxUser: {},
      tel: '',
      yzm: '',
      binding: false,
      sending: false,
      countdown: RESEND_SECONDS,
      pollTimer: null,
      countdownTimer: null,
      // 绑定成功后的续跑回调（扫码付款场景传入；缺省时回退为「提示 + 整页刷新」）
      pendingOnBound: null
    }
  },
  computed: {
    qrTip () {
      return isWechatBrowser()
        ? '长按上方二维码识别，关注公众号即完成微信授权'
        : '请使用微信扫描上方二维码完成授权'
    },
    sendText () {
      return this.countdown < RESEND_SECONDS ? `（${this.countdown}）后重发` : '发送验证码'
    }
  },
  created () {
    // 注册时可接收「绑定成功后的续跑回调」：扫码付款场景由 ensureScanPayBound 传入
    registerWechatBindDialog(onBound => this.open(onBound))
  },
  beforeDestroy () {
    registerWechatBindDialog(null)
    this.clearAllTimers()
  },
  methods: {
    /** 由 utils/wechatPay.openWechatBindDialog() 唤起 */
    open (onBound) {
      this.step = 'qrcode'
      this.wxUser = {}
      this.yzm = ''
      this.tel = ''
      // 扫码付款场景：绑定成功后就地续跑原付款流程（不整页刷新）
      this.pendingOnBound = typeof onBound === 'function' ? onBound : null
      const user = readCurrentUser()
      if (user && user.tel) {
        this.tel = user.tel
      }
      this.dialogVisible = true
      this.startQrcode()
    },
    /** 拉取二维码票据并开始轮询扫码结果 */
    async startQrcode () {
      this.clearPollTimer()
      this.ticket = ''
      this.qrImg = ''
      try {
        const res = await apiReq.getQrcode()
        const ticket = res && res.ticket
        if (!ticket) {
          Message.error('二维码获取失败，请稍后重试')
          return
        }
        this.ticket = ticket
        this.qrImg = 'https://mp.weixin.qq.com/cgi-bin/showqrcode?ticket=' + ticket
        this.pollTimer = setInterval(this.pollScan, POLL_INTERVAL)
      } catch (error) {
        console.error('[WechatBindDialog] 获取二维码失败:', error)
        Message.error('二维码获取失败，请稍后重试')
      }
    },
    /** 轮询扫码结果：拿到微信账号即进入绑定手机号步骤 */
    async pollScan () {
      if (!this.ticket) {
        return
      }
      try {
        const res = await apiReq.getLunxun2({ ticket: this.ticket })
        if (res && res.code === '1111' && res.data && res.data.open_id) {
          this.clearPollTimer()
          this.wxUser = res.data
          if (res.data.tel && !this.tel) {
            this.tel = res.data.tel
          }
          this.step = 'phone'
        }
      } catch (error) {
        // 轮询期间偶发失败不打断流程，下一轮继续
      }
    },
    restartQrcode () {
      this.step = 'qrcode'
      this.wxUser = {}
      this.yzm = ''
      this.startQrcode()
    },
    /** 发送绑定用短信验证码 */
    async sendCode () {
      if (this.sending) {
        return
      }
      if (!this.isValidPhone(this.tel)) {
        Message.error('请输入正确的手机号')
        return
      }
      this.sending = true
      try {
        const res = await apiReq.send({ phone: this.tel })
        if (res && res.code === '1111') {
          Message.success('验证码已发送')
          this.startCountdown()
        } else {
          Message.error((res && (res.msg || res.data)) || '验证码发送失败，请稍后重试')
        }
      } catch (error) {
        console.error('[WechatBindDialog] 发送验证码失败:', error)
        Message.error('验证码发送失败，请稍后重试')
      } finally {
        this.sending = false
      }
    },
    startCountdown () {
      this.clearCountdownTimer()
      this.countdown = RESEND_SECONDS
      this.countdownTimer = setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          this.clearCountdownTimer()
          this.countdown = RESEND_SECONDS
        }
      }, 1000)
    },
    /**
     * 提交绑定：成功后写入登录态。
     * - 有 pendingOnBound（扫码付款场景）→ 不刷新，回调续跑原付款流程；
     * - 无 pendingOnBound（微信内 JSAPI 等）→ 保持原行为：提示 + 1.5s 整页刷新。
     */
    async submitBind () {
      if (!this.wxUser.open_id) {
        Message.error('微信授权已失效，请重新扫码')
        this.restartQrcode()
        return
      }
      if (!this.isValidPhone(this.tel)) {
        Message.error('请输入正确的手机号')
        return
      }
      if (!this.yzm) {
        Message.error('请输入验证码')
        return
      }
      this.binding = true
      try {
        const res = await apiReq.telBind({
          openid: this.wxUser.open_id,
          tel: this.tel,
          yzm: this.yzm
        })
        if (res && res.code === '1111' && res.data) {
          localStorage.setItem('user', JSON.stringify(res.data))
          const resume = this.pendingOnBound
          this.pendingOnBound = null
          this.clearAllTimers()
          this.dialogVisible = false
          if (resume) {
            // 扫码付款场景：不整页刷新，就地续跑原付款流程（重新生成付款码）
            Message.success(SCAN_PAY_TEXT.bindSuccessResume)
            setTimeout(() => resume(), 300)
          } else {
            // 微信内 JSAPI 等场景：保持原行为（提示 + 整页刷新）
            Message.success(res.msg || SCAN_PAY_TEXT.bindSuccess)
            setTimeout(() => window.location.reload(), 1500)
          }
        } else {
          Message.error((res && (res.msg || res.data)) || '绑定失败，请稍后重试')
        }
      } catch (error) {
        console.error('[WechatBindDialog] 绑定微信失败:', error)
        Message.error('绑定失败，请稍后重试')
      } finally {
        this.binding = false
      }
    },
    isValidPhone (tel) {
      return /^1[3-9]\d{9}$/.test(tel || '')
    },
    handleClose () {
      // 扫码付款场景下未完成绑定就关闭：给「绑定后才能扫码付款」的明确提示
      if (this.pendingOnBound) {
        this.pendingOnBound = null
        Message.warning(SCAN_PAY_TEXT.bindGiveUp)
      }
      this.dialogVisible = false
    },
    clearPollTimer () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    clearCountdownTimer () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    clearAllTimers () {
      this.clearPollTimer()
      this.clearCountdownTimer()
      this.countdown = RESEND_SECONDS
    }
  },
  watch: {
    dialogVisible (val) {
      if (!val) {
        this.clearAllTimers()
      }
    }
  }
}
</script>

<style scoped>
.wxbind-intro {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
}

.wxbind-body {
  text-align: center;
}

.wxbind-qr {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  margin: 0 auto;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}

.wxbind-qr img {
  display: block;
  width: 100%;
  height: 100%;
}

.wxbind-qr-loading {
  font-size: 13px;
  color: #909399;
}

.wxbind-tip {
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: #909399;
}

.wxbind-field {
  display: flex;
  align-items: center;
  margin: 12px 0 0;
}

.wxbind-field dt {
  width: 70px;
  text-align: left;
  font-size: 14px;
  color: #606266;
}

.wxbind-field dd {
  flex: 1;
  margin: 0;
}

.wxbind-code {
  display: flex;
  align-items: center;
}

.wxbind-send {
  flex: none;
  margin-left: 10px;
  font-size: 13px;
  color: #c79745;
  cursor: pointer;
  white-space: nowrap;
}

.wxbind-send.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.wxbind-note {
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.7;
  color: #e6a23c;
  text-align: left;
}

.wxbind-actions {
  margin-top: 18px;
  text-align: right;
}
</style>
